import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "@/components/motion";
import { useLang } from "@/context/LangContext";

export const TissueTeaser = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-30, 50]);
  const { t } = useLang();
  return (
    <section ref={ref} data-testid="home-tissue" className="relative overflow-hidden bg-[var(--leaf-deep)] py-24 sm:py-32 text-white grain">
      <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow !text-[#9be0b0]">Tissue Culture Collection</p>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight">{t("Era baru", "A new era")}<br />{t("propagasi tanaman.", "of plant propagation.")}</h2>
          <p className="mt-6 max-w-lg text-white/85 leading-relaxed">{t("Tanaman kultur jaringan umumnya bebas patogen, hama, dan penyakit. Kondisi steril laboratorium kami memastikan bibit yang bersih — siap ekspor dalam jumlah grosir.", "Tissue culture plants are generally free from pathogens, pests and diseases. The sterile conditions of our laboratory ensure clean starting material — export-ready in wholesale quantities.")}</p>
          <Link to="/kultur-jaringan" data-testid="home-tissue-cta" className="group mt-9 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-display text-sm font-bold text-[var(--leaf-deep)]">
            {t("Lihat 46 varietas ekspor", "View 46 export varieties")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </Reveal>
        <div className="relative h-[420px] sm:h-[520px]">
          <motion.div style={{ y: y1 }} className="absolute left-0 top-0 h-[78%] w-[58%] overflow-hidden arch">
            <img src="/assets/img/tc-lab-1.jpg" alt="Tim laboratorium kultur jaringan Minaqu" className="h-full w-full object-cover" />
          </motion.div>
          <motion.div style={{ y: y2 }} className="absolute bottom-0 right-0 h-[62%] w-[48%] overflow-hidden rounded-[2rem] ring-8 ring-[var(--leaf-deep)]">
            <img src="/assets/img/tc-lab-3.jpg" alt="Plantlet kultur jaringan" className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const SouvenirTeaser = () => {
  const { t } = useLang();
  return (
  <section data-testid="home-souvenir" className="bg-[var(--cream)] py-24 sm:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal className="text-center">
        <p className="eyebrow !text-[var(--forest)]">Series of Hampers & Souvenirs 2025</p>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[var(--forest)]">A little green for your soul.</h2>
      </Reveal>
      <div className="mx-auto mt-14 grid max-w-4xl grid-cols-3 gap-3 sm:gap-6">
        {[["kelana", "Kelana"], ["aluna", "Aluna"], ["gardenia", "Gardenia"]].map(([k, n], i) => (
          <Reveal key={k} delay={i * 0.1} className={i === 1 ? "sm:-mt-10" : "sm:mt-6"}>
            <div className="aspect-[3/5] overflow-hidden arch bg-[var(--forest)]">
              <img src={`/assets/img/${k}.jpg`} alt={`Hampers ${n}`} className="h-full w-full scale-[1.12] object-cover" />
            </div>
            <p className="mt-4 text-center font-display text-sm sm:text-xl font-black tracking-[0.15em] text-[var(--forest)]">{n.toUpperCase()}</p>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12 flex justify-center">
        <Link to="/souvenir-hampers" data-testid="home-souvenir-cta" className="group inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-6 py-3.5 font-display text-sm font-bold text-white">
          {t("Jelajahi souvenir & hampers", "Explore souvenirs & hampers")} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
        </Link>
      </Reveal>
    </div>
  </section>
  );
};
